// Enhanced Agency Registration Component with Tax Forms for Travel+ CRM
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import W9Form from '../TaxForms/W9Form';
import W8BENForm from '../TaxForms/W8BENForm';
import { MapPin, Building, FileText, Check } from 'lucide-react';

export const AgencyRegistrationWithTax = ({ agency, agencyCode, onSuccess }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // 1: Personal Info, 2: Tax Form, 3: Review
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    agencyCode: agencyCode,
    agencyName: agency,
    agreeToTerms: false
  });

  const [taxFormData, setTaxFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [agencyTerms, setAgencyTerms] = useState('');

  // List of countries for the dropdown
  const countries = [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'AT', name: 'Austria' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' },
    { code: 'IE', name: 'Ireland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'GR', name: 'Greece' },
    { code: 'JP', name: 'Japan' },
    { code: 'KR', name: 'South Korea' },
    { code: 'SG', name: 'Singapore' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'MX', name: 'Mexico' },
    { code: 'BR', name: 'Brazil' },
    { code: 'AR', name: 'Argentina' },
    { code: 'CL', name: 'Chile' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'IN', name: 'India' },
    { code: 'CN', name: 'China' },
    { code: 'TH', name: 'Thailand' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'PH', name: 'Philippines' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'IL', name: 'Israel' },
    { code: 'TR', name: 'Turkey' },
    { code: 'EG', name: 'Egypt' },
    { code: 'MA', name: 'Morocco' },
    { code: 'KE', name: 'Kenya' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'GH', name: 'Ghana' },
    { code: 'OTHER', name: 'Other' }
  ];

  // Fetch agency-specific terms
  useEffect(() => {
    const fetchAgencyTerms = async () => {
      try {
        const response = await api.get(`/agencies/${agencyCode}/terms`);
        setAgencyTerms(response.data.terms);
      } catch (error) {
        console.log('Using default terms');
      }
    };
    
    if (agencyCode) {
      fetchAgencyTerms();
    }
  }, [agencyCode]);

  const validatePersonalInfo = () => {
    const newErrors = {};
    
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (currentStep === 1 && validatePersonalInfo()) {
      setCurrentStep(2);
    }
  };

  const handleTaxFormComplete = (taxData) => {
    setTaxFormData(taxData);
    setCurrentStep(3);
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    setErrors({});

    try {
      // Combine all data for submission
      const submissionData = {
        ...formData,
        taxFormData: taxFormData,
        taxFormType: formData.country === 'US' ? 'W-9' : 'W-8BEN'
      };

      const response = await api.post('/registration/agency', submissionData);
      // Navigate to success page instead of showing inline success
      navigate('/registration-success', {
        state: { agency, agencyCode }
      });
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorObj = {};
        error.response.data.errors.forEach(err => {
          errorObj[err.param] = err.msg;
        });
        setErrors(errorObj);
      } else {
        setErrors({ general: error.response?.data?.message || 'Registration failed' });
      }
      // Don't go back to step 1 - stay on current step to show error
    } finally {
      setLoading(false);
    }
  };

  // Remove the submitted check as we're navigating away

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-center">
        <div className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
            currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
          }`}>
            {currentStep > 1 ? <Check className="w-5 h-5" /> : '1'}
          </div>
          <span className="ml-2 text-sm font-medium text-gray-900">Personal Info</span>
        </div>
        
        <div className="mx-4 w-16 h-1 bg-gray-300">
          <div className={`h-full bg-blue-600 transition-all ${
            currentStep >= 2 ? 'w-full' : 'w-0'
          }`} />
        </div>
        
        <div className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
            currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
          }`}>
            {currentStep > 2 ? <Check className="w-5 h-5" /> : '2'}
          </div>
          <span className="ml-2 text-sm font-medium text-gray-900">Tax Form</span>
        </div>
        
        <div className="mx-4 w-16 h-1 bg-gray-300">
          <div className={`h-full bg-blue-600 transition-all ${
            currentStep >= 3 ? 'w-full' : 'w-0'
          }`} />
        </div>
        
        <div className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
            currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
          }`}>
            3
          </div>
          <span className="ml-2 text-sm font-medium text-gray-900">Review & Submit</span>
        </div>
      </div>
    </div>
  );

  const renderPersonalInfoStep = () => (
    <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Apply to Join {agency}</h2>
        <p className="text-gray-600">Please fill out the following information to apply as an agent.</p>
      </div>

      {errors.general && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{errors.general}</p>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }} className="space-y-6">
        
        {/* Personal Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Personal Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input 
                type="text" 
                required
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({...prev, firstName: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.firstName ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.firstName && <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input 
                type="text" 
                required
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({...prev, lastName: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.lastName ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.lastName && <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input 
                type="email" 
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input 
                type="tel" 
                required
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({...prev, phone: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.phone ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone}</p>}
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
            <MapPin className="w-5 h-5 mr-2" />
            Address Information
          </h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Street Address
            </label>
            <input 
              type="text"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({...prev, address: e.target.value}))}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.address ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="123 Main Street"
            />
            {errors.address && <p className="text-red-600 text-sm mt-1">{errors.address}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input 
                type="text"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({...prev, city: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.city ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="City"
              />
              {errors.city && <p className="text-red-600 text-sm mt-1">{errors.city}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State/Province
              </label>
              <input 
                type="text"
                value={formData.state}
                onChange={(e) => setFormData(prev => ({...prev, state: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.state ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="State/Province"
              />
              {errors.state && <p className="text-red-600 text-sm mt-1">{errors.state}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP/Postal Code
              </label>
              <input 
                type="text"
                value={formData.zipCode}
                onChange={(e) => setFormData(prev => ({...prev, zipCode: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.zipCode ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="ZIP/Postal Code"
              />
              {errors.zipCode && <p className="text-red-600 text-sm mt-1">{errors.zipCode}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country *
              </label>
              <select
                required
                value={formData.country}
                onChange={(e) => setFormData(prev => ({...prev, country: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.country ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select Country</option>
                {countries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
              {errors.country && <p className="text-red-600 text-sm mt-1">{errors.country}</p>}
            </div>
          </div>

          {/* Tax Form Notice */}
          {formData.country && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Tax Information:</strong> 
                {formData.country === 'US' ? 
                  ' You will need to complete a W-9 tax form in the next step.' :
                  ' You will need to complete a W-8BEN tax form in the next step.'
                }
              </p>
            </div>
          )}
        </div>

        {/* Agency Terms and Conditions */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
            <Building className="w-5 h-5 mr-2" />
            Agent Agreement
          </h3>
          
          <div className="bg-gray-50 border rounded-lg p-4 max-h-40 overflow-y-auto">
            <h4 className="font-medium text-gray-900 mb-2">{agency} Agent Agreement</h4>
            <div className="text-sm text-gray-700 space-y-2">
              {agencyTerms ? (
                <div dangerouslySetInnerHTML={{ __html: agencyTerms }} />
              ) : (
                <div>
                  <p>By applying to join {agency}, you agree to:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Maintain professional standards in all client interactions</li>
                    <li>Follow agency policies and procedures</li>
                    <li>Complete required training and certifications</li>
                    <li>Adhere to commission structure and payment terms</li>
                    <li>Represent the agency with integrity and professionalism</li>
                    <li>Comply with all industry regulations and licensing requirements</li>
                    <li>Complete required tax forms (W-9 for US agents, W-8BEN for international agents)</li>
                  </ul>
                  <p className="font-medium mt-2">Full terms and conditions will be provided upon approval.</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start">
            <input
              type="checkbox"
              required
              checked={formData.agreeToTerms}
              onChange={(e) => setFormData(prev => ({...prev, agreeToTerms: e.target.checked}))}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
            />
            <label className="ml-2 text-sm text-gray-700">
              I have read and agree to the {agency} Agent Agreement and Terms of Service *
            </label>
          </div>
          {errors.agreeToTerms && <p className="text-red-600 text-sm mt-1">{errors.agreeToTerms}</p>}
        </div>

        {/* Next Button */}
        <div className="pt-6">
          <button 
            type="submit"
            disabled={!formData.agreeToTerms}
            className={`w-full py-3 px-4 rounded-md text-white font-medium transition-colors ${
              !formData.agreeToTerms
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
            }`}
          >
            Next: Complete Tax Form
          </button>
        </div>
      </form>
    </div>
  );

  const renderTaxFormStep = () => {
    // Create a temporary user object for the tax form components
    const tempUser = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      country: formData.country
    };

    return (
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6 sm:p-8 border-b">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <FileText className="w-6 h-6 mr-2" />
            Tax Form - {formData.country === 'US' ? 'W-9' : 'W-8BEN'}
          </h2>
          <p className="text-gray-600 mt-2">
            Please complete the required tax form to continue with your application.
          </p>
        </div>
        
        <div className="p-6 sm:p-8">
          {formData.country === 'US' ? (
            <W9FormRegistration 
              user={tempUser}
              registrationData={formData}
              onComplete={handleTaxFormComplete}
              onBack={() => setCurrentStep(1)}
            />
          ) : (
            <W8BENFormRegistration
              user={tempUser}
              registrationData={formData}
              onComplete={handleTaxFormComplete}
              onBack={() => setCurrentStep(1)}
            />
          )}
        </div>
      </div>
    );
  };

  const renderReviewStep = () => (
    <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Your Application</h2>
        <p className="text-gray-600">Please review your information before submitting.</p>
      </div>

      <div className="space-y-6">
        {/* Personal Information Review */}
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Name:</span>
              <span className="ml-2 text-gray-900">{formData.firstName} {formData.lastName}</span>
            </div>
            <div>
              <span className="text-gray-600">Email:</span>
              <span className="ml-2 text-gray-900">{formData.email}</span>
            </div>
            <div>
              <span className="text-gray-600">Phone:</span>
              <span className="ml-2 text-gray-900">{formData.phone}</span>
            </div>
            <div>
              <span className="text-gray-600">Country:</span>
              <span className="ml-2 text-gray-900">
                {countries.find(c => c.code === formData.country)?.name}
              </span>
            </div>
          </div>
        </div>

        {/* Address Information Review */}
        {(formData.address || formData.city) && (
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Address Information</h3>
            <div className="text-sm text-gray-900">
              {formData.address && <div>{formData.address}</div>}
              <div>
                {formData.city && formData.city}
                {formData.city && formData.state && ', '}
                {formData.state && formData.state}
                {formData.zipCode && ` ${formData.zipCode}`}
              </div>
            </div>
          </div>
        )}

        {/* Tax Form Review */}
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Tax Form</h3>
          <div className="text-sm">
            <span className="text-gray-600">Form Type:</span>
            <span className="ml-2 text-gray-900">
              {formData.country === 'US' ? 'W-9' : 'W-8BEN'} - Completed ✓
            </span>
          </div>
        </div>

        {/* Agreement Review */}
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Agent Agreement</h3>
          <div className="text-sm">
            <span className="text-gray-600">Status:</span>
            <span className="ml-2 text-green-600 font-medium">Agreed ✓</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-6">
          <button
            type="button"
            onClick={() => setCurrentStep(1)}
            className="flex-1 py-3 px-4 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 font-medium transition-colors"
          >
            Edit Information
          </button>
          <button
            type="button"
            onClick={handleFinalSubmit}
            disabled={loading}
            className={`flex-1 py-3 px-4 rounded-md text-white font-medium transition-colors ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500'
            }`}
          >
            {loading ? 'Submitting Application...' : 'Submit Application'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {renderStepIndicator()}
      
      {currentStep === 1 && renderPersonalInfoStep()}
      {currentStep === 2 && renderTaxFormStep()}
      {currentStep === 3 && renderReviewStep()}
    </div>
  );
};

// W9 Form wrapper for registration
const W9FormRegistration = ({ user, registrationData, onComplete, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Pre-fill from registration data
    name: `${registrationData.firstName} ${registrationData.lastName}`.trim(),
    businessName: '',
    federalTaxClassification: 'individual',
    llcClassification: '',
    otherDescription: '',
    exemptPayeeCode: '',
    exemptionFromFATCA: '',
    
    // Pre-fill address from registration
    address: registrationData.address || '',
    city: registrationData.city || '',
    state: registrationData.state || '',
    zipCode: registrationData.zipCode || '',
    
    // Requester info
    requesterName: 'Travel+ CRM',
    requesterAddress: '929 W. Sunset Blvd. STE 21-292, St. George, UT 84770',
    accountNumbers: '',
    
    // TIN
    tinType: 'ssn',
    ssn1: '', ssn2: '', ssn3: '',
    ein1: '', ein2: '',
    
    // Certification
    certification1: false,
    certification2: false,
    certification3: false,
    certification4: false,
    
    // Signature
    signature: '',
    signatureDate: new Date().toISOString().split('T')[0]
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.federalTaxClassification) newErrors.federalTaxClassification = 'Tax classification is required';
    if (!formData.address) newErrors.address = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.zipCode) newErrors.zipCode = 'ZIP code is required';
    
    if (formData.tinType === 'ssn') {
      if (!formData.ssn1 || !formData.ssn2 || !formData.ssn3) {
        newErrors.ssn = 'SSN is required';
      } else if (formData.ssn1.length !== 3 || formData.ssn2.length !== 2 || formData.ssn3.length !== 4) {
        newErrors.ssn = 'Invalid SSN format';
      }
    } else {
      if (!formData.ein1 || !formData.ein2) {
        newErrors.ein = 'EIN is required';
      } else if (formData.ein1.length !== 2 || formData.ein2.length !== 7) {
        newErrors.ein = 'Invalid EIN format';
      }
    }
    
    if (!formData.certification1) newErrors.certification1 = 'You must certify backup withholding status';
    if (!formData.certification2) newErrors.certification2 = 'You must certify US person status';
    if (!formData.certification3) newErrors.certification3 = 'You must certify TIN accuracy';
    if (!formData.signature) newErrors.signature = 'Signature is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const tin = formData.tinType === 'ssn' 
        ? `${formData.ssn1}-${formData.ssn2}-${formData.ssn3}`
        : `${formData.ein1}-${formData.ein2}`;
      
      const taxFormData = {
        form_type: 'W-9',
        form_data: {
          ...formData,
          tin,
          signatureDate: new Date().toISOString()
        },
        digital_signature: formData.signature
      };
      
      onComplete(taxFormData);
    } catch (error) {
      console.error('Form preparation error:', error);
      setErrors({ submit: 'Failed to prepare form. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Form W-9: Request for Taxpayer Identification Number and Certification
        </h3>
        <p className="text-gray-600">
          Complete this form as part of your agent registration.
        </p>
      </div>

      {/* Part I - Identification */}
      <div className="border rounded-lg p-6">
        <h4 className="text-lg font-semibold mb-4">Part I - Taxpayer Identification</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              1. Name (as shown on your income tax return) *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              2. Business name/disregarded entity name, if different from above
            </label>
            <input
              type="text"
              name="businessName"
              value={formData.businessName}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              3. Federal tax classification *
            </label>
            <div className="space-y-2">
              {[
                { value: 'individual', label: 'Individual/sole proprietor or single-member LLC' },
                { value: 'c-corp', label: 'C Corporation' },
                { value: 's-corp', label: 'S Corporation' },
                { value: 'partnership', label: 'Partnership' },
                { value: 'trust', label: 'Trust/estate' },
                { value: 'llc', label: 'Limited liability company' },
                { value: 'other', label: 'Other' }
              ].map(option => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    name="federalTaxClassification"
                    value={option.value}
                    checked={formData.federalTaxClassification === option.value}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
            {errors.federalTaxClassification && (
              <p className="text-red-500 text-sm mt-1">{errors.federalTaxClassification}</p>
            )}
          </div>
        </div>
      </div>

      {/* Part II - Address */}
      <div className="border rounded-lg p-6">
        <h4 className="text-lg font-semibold mb-4">Part II - Address</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              5. Address (number, street, and apt. or suite no.) *
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md ${errors.address ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                6. City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md ${errors.city ? 'border-red-500' : 'border-gray-300'}`}
                required
              />
              {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State *
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                maxLength="2"
                placeholder="ST"
                className={`w-full px-3 py-2 border rounded-md ${errors.state ? 'border-red-500' : 'border-gray-300'}`}
                required
              />
              {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
            </div>
          </div>

          <div className="w-1/2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ZIP code *
            </label>
            <input
              type="text"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleInputChange}
              maxLength="10"
              className={`w-full px-3 py-2 border rounded-md ${errors.zipCode ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
          </div>
        </div>
      </div>

      {/* Part III - TIN */}
      <div className="border rounded-lg p-6">
        <h4 className="text-lg font-semibold mb-4">Part III - Taxpayer Identification Number (TIN)</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select TIN type *
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="tinType"
                value="ssn"
                checked={formData.tinType === 'ssn'}
                onChange={handleInputChange}
                className="mr-2"
              />
              <span>Social Security Number (SSN)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="tinType"
                value="ein"
                checked={formData.tinType === 'ein'}
                onChange={handleInputChange}
                className="mr-2"
              />
              <span>Employer Identification Number (EIN)</span>
            </label>
          </div>
        </div>

        {formData.tinType === 'ssn' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Social Security Number *
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                name="ssn1"
                value={formData.ssn1}
                onChange={handleInputChange}
                maxLength="3"
                placeholder="XXX"
                className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center"
              />
              <span className="self-center">-</span>
              <input
                type="text"
                name="ssn2"
                value={formData.ssn2}
                onChange={handleInputChange}
                maxLength="2"
                placeholder="XX"
                className="w-16 px-3 py-2 border border-gray-300 rounded-md text-center"
              />
              <span className="self-center">-</span>
              <input
                type="text"
                name="ssn3"
                value={formData.ssn3}
                onChange={handleInputChange}
                maxLength="4"
                placeholder="XXXX"
                className="w-24 px-3 py-2 border border-gray-300 rounded-md text-center"
              />
            </div>
            {errors.ssn && <p className="text-red-500 text-sm mt-1">{errors.ssn}</p>}
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employer Identification Number *
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                name="ein1"
                value={formData.ein1}
                onChange={handleInputChange}
                maxLength="2"
                placeholder="XX"
                className="w-16 px-3 py-2 border border-gray-300 rounded-md text-center"
              />
              <span className="self-center">-</span>
              <input
                type="text"
                name="ein2"
                value={formData.ein2}
                onChange={handleInputChange}
                maxLength="7"
                placeholder="XXXXXXX"
                className="w-32 px-3 py-2 border border-gray-300 rounded-md text-center"
              />
            </div>
            {errors.ein && <p className="text-red-500 text-sm mt-1">{errors.ein}</p>}
          </div>
        )}
      </div>

      {/* Part IV - Certification */}
      <div className="border rounded-lg p-6">
        <h4 className="text-lg font-semibold mb-4">Part IV - Certification</h4>
        
        <p className="text-sm text-gray-600 mb-4">
          Under penalties of perjury, I certify that:
        </p>
        
        <div className="space-y-3">
          <label className="flex items-start">
            <input
              type="checkbox"
              name="certification1"
              checked={formData.certification1}
              onChange={handleInputChange}
              className="mt-1 mr-3"
            />
            <span className="text-sm">
              1. The number shown on this form is my correct taxpayer identification number, and
            </span>
          </label>
          {errors.certification1 && <p className="text-red-500 text-sm ml-6">{errors.certification1}</p>}

          <label className="flex items-start">
            <input
              type="checkbox"
              name="certification2"
              checked={formData.certification2}
              onChange={handleInputChange}
              className="mt-1 mr-3"
            />
            <span className="text-sm">
              2. I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified by the IRS that I am subject to backup withholding, and
            </span>
          </label>
          {errors.certification2 && <p className="text-red-500 text-sm ml-6">{errors.certification2}</p>}

          <label className="flex items-start">
            <input
              type="checkbox"
              name="certification3"
              checked={formData.certification3}
              onChange={handleInputChange}
              className="mt-1 mr-3"
            />
            <span className="text-sm">
              3. I am a U.S. citizen or other U.S. person, and
            </span>
          </label>
          {errors.certification3 && <p className="text-red-500 text-sm ml-6">{errors.certification3}</p>}

          <label className="flex items-start">
            <input
              type="checkbox"
              name="certification4"
              checked={formData.certification4}
              onChange={handleInputChange}
              className="mt-1 mr-3"
            />
            <span className="text-sm">
              4. The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.
            </span>
          </label>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Signature *
          </label>
          <input
            type="text"
            name="signature"
            value={formData.signature}
            onChange={handleInputChange}
            placeholder="Type your full name as digital signature"
            className={`w-full px-3 py-2 border rounded-md ${errors.signature ? 'border-red-500' : 'border-gray-300'}`}
            required
          />
          {errors.signature && <p className="text-red-500 text-sm mt-1">{errors.signature}</p>}
          
          <p className="text-sm text-gray-500 mt-2">
            Date: {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>

      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{errors.submit}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3 px-4 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 font-medium transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading}
          className={`flex-1 py-3 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {loading ? 'Processing...' : 'Continue'}
        </button>
      </div>
    </form>
  );
};

// W8BEN Form wrapper for registration (similar structure)
const W8BENFormRegistration = ({ user, registrationData, onComplete, onBack }) => {
  // This would be similar to W9FormRegistration but with W-8BEN fields
  // For brevity, I'll just include the essential structure
  const handleSubmit = (e) => {
    e.preventDefault();
    // Process W-8BEN form data
    const taxFormData = {
      form_type: 'W-8BEN',
      form_data: {
        // W-8BEN specific fields
      },
      digital_signature: 'signature'
    };
    onComplete(taxFormData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Form W-8BEN: Certificate of Foreign Status
        </h3>
        <p className="text-gray-600">
          Complete this form as part of your agent registration.
        </p>
      </div>
      
      {/* W-8BEN form fields would go here */}
      <p className="text-center text-gray-600 py-8">
        W-8BEN form implementation would include all required fields for international agents.
      </p>
      
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3 px-4 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 font-medium transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Continue
        </button>
      </div>
    </form>
  );
};

export default AgencyRegistrationWithTax;
